import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../AuthContext';
import { notificationsAPI } from '../../api';
import { formatRelativeTime } from '../../utils';
import {
  Bell,
  Mail,
  Award,
  TrendingUp,
  Target,
  Gift,
  Users2,
  MessageCircle,
  AtSign,
  Info,
  Loader2,
  Inbox,
} from 'lucide-react';

/**
 * NotificationBell — top-bar bell with unread badge + dropdown feed.
 *
 * Performance Club dark surface (matches the rest of the app after
 * PR #23 / DirectMessages). Polls /api/notifications/unread-count
 * every 10s so the badge stays in sync even when the user is on a
 * different page. When the user opens the dropdown, it loads the
 * latest 10 notifications and shows them; clicking one optimistically
 * marks it read and follows the `link` field if present.
 *
 * Both a default export (for <NotificationBell />) and a named export
 * (for { NotificationBell }) are exposed.
 */
export function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [markingId, setMarkingId] = useState(null);
  const containerRef = useRef(null);

  // Poll unread count once on mount as a baseline. The live updates come
  // from the SSE stream below — polling is only a safety net.
  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await notificationsAPI.getUnreadCount();
      setUnreadCount(res.data?.unread_count ?? 0);
    } catch (err) {
      // Silent — the bell just keeps its last known count.
      console.error('Failed to fetch unread count:', err);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // -------------------------------------------------------------------------
  // SSE stream: real-time push of new notifications.
  // -------------------------------------------------------------------------
  // The browser's EventSource handles auto-reconnect. We update the unread
  // count on every `notification` event and, if the dropdown is open, also
  // prepend the new item to the visible list. The stream is held open as
  // long as the component is mounted; closing the bell does not disconnect
  // — the user wants to see new notifications arrive in real time even
  // when the dropdown is closed.
  useEffect(() => {
    if (!token) return undefined;
    // EventSource doesn't support custom headers, so we pass the JWT in
    // the query string. The backend /api/notifications/stream route
    // accepts `?token=<jwt>` for exactly this reason. Same-origin means
    // the token stays in the browser; the server only logs paths and
    // query params in access logs, which the operator can redact.
    const es = new EventSource(`/api/notifications/stream?token=${encodeURIComponent(token)}`);
    es.addEventListener('connected', () => {
      // Handshake received; connection live.
    });
    es.addEventListener('notification', (e) => {
      try {
        const n = JSON.parse(e.data);
        // Optimistic UI: bump the badge and prepend to the list if open.
        setUnreadCount((c) => c + 1);
        setItems((prev) => {
          if (prev.some((x) => x.id === n.id)) return prev;
          return [n, ...prev].slice(0, 10);
        });
      } catch {
        // Malformed event — ignore.
      }
    });
    es.addEventListener('ping', () => {
      // Keepalive. Nothing to do.
    });
    es.onerror = () => {
      // Browser EventSource handles reconnect on its own. We just log so
      // debugging is easier when the connection drops.
      console.warn('SSE connection error; browser will retry');
    };
    return () => es.close();
  }, [token]);

  // Fetch the latest 10 notifications when the dropdown opens.
  const fetchLatest = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationsAPI.list({ limit: 10, skip: 0 });
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchLatest();
  }, [open, fetchLatest]);

  // Close dropdown on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleClickItem = async (n) => {
    if (markingId) return;
    if (!n.is_read) {
      // Optimistic: flip locally first, then confirm with the server.
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      setUnreadCount((c) => Math.max(0, c - 1));
      setMarkingId(n.id);
      try {
        await notificationsAPI.markRead(n.id);
      } catch (err) {
        // Roll back if the server rejects.
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: false } : x)));
        setUnreadCount((c) => c + 1);
        console.error('Failed to mark notification read:', err);
      } finally {
        setMarkingId(null);
      }
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const showBadge = unreadCount > 0;
  const badgeText = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <div className="relative" ref={containerRef} data-testid="notification-bell">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] hover:bg-white/[0.08] transition-colors"
        aria-label={t('notificationsBell.open', { defaultValue: 'Open notifications' })}
        data-testid="notification-bell-button"
      >
        <Bell className="w-5 h-5" />
        {showBadge && (
          <span
            className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-primary-200 text-[#0a1208] text-[10px] font-semibold rounded-full flex items-center justify-center"
            data-testid="notification-bell-badge"
          >
            {badgeText}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[22rem] max-w-[calc(100vw-1.5rem)] z-50 rounded-2xl border border-[var(--color-border)] bg-[rgba(6,12,10,0.96)] shadow-[var(--color-shadow)] backdrop-blur-xl overflow-hidden"
          data-testid="notification-bell-dropdown"
        >
          <div className="p-3 border-b border-[var(--color-border)] flex items-center justify-between">
            <div>
              <span className="hero-kicker">{t('notificationsBell.kicker', { defaultValue: 'Notifications' })}</span>
              <p className="mt-1 font-display text-lg text-[var(--color-text)]">
                {t('notificationsBell.title', { defaultValue: 'Recent activity' })}
              </p>
            </div>
            {showBadge && (
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-primary-200">
                {t('notificationsBell.unread', {
                  defaultValue: `${unreadCount} unread`,
                  count: unreadCount,
                })}
              </span>
            )}
          </div>

          <div className="max-h-[26rem] overflow-y-auto" data-testid="notification-bell-list">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-primary-200" />
              </div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center" data-testid="notification-bell-empty">
                <div className="mx-auto h-12 w-12 rounded-2xl border border-[var(--color-border)] bg-white/[0.04] flex items-center justify-center mb-3">
                  <Inbox className="w-6 h-6 text-[var(--color-text-muted)]" />
                </div>
                <p className="text-sm text-[var(--color-text-muted)]">
                  {t('notificationsBell.empty', { defaultValue: "All caught up" })}
                </p>
                <p className="text-xs text-[var(--color-text-muted)] mt-1">
                  {t('notificationsBell.emptyHint', {
                    defaultValue: "We'll let you know when something happens",
                  })}
                </p>
              </div>
            ) : (
              items.map((n) => {
                const meta = TYPE_META[n.type] || TYPE_META.system;
                const Icon = meta.icon;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => handleClickItem(n)}
                    className={`w-full text-left p-3 flex gap-3 border-b border-[var(--color-border)] last:border-b-0 transition-colors hover:bg-white/[0.04] ${
                      n.is_read ? 'opacity-70' : ''
                    }`}
                    data-testid={`notification-bell-item-${n.id}`}
                  >
                    <div
                      className={`shrink-0 h-9 w-9 rounded-xl border flex items-center justify-center ${
                        n.is_read
                          ? 'border-[var(--color-border)] bg-white/[0.04]'
                          : 'border-[rgba(213,255,123,0.25)] bg-[rgba(213,255,123,0.08)]'
                      }`}
                    >
                      <Icon className="w-4 h-4 text-primary-100" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="text-sm font-semibold text-[var(--color-text)] truncate">
                          {n.title}
                        </p>
                        <span className="shrink-0 text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
                          {formatRelativeTime(n.created_at)}
                        </span>
                      </div>
                      <p className="text-xs text-[var(--color-text-muted)] mt-0.5 line-clamp-2">
                        {n.body}
                      </p>
                    </div>
                    {!n.is_read && (
                      <span
                        className="shrink-0 self-center h-2 w-2 rounded-full bg-primary-300"
                        aria-label={t('notificationsBell.unreadDot', { defaultValue: 'Unread' })}
                      />
                    )}
                  </button>
                );
              })
            )}
          </div>

          <div className="p-2 border-t border-[var(--color-border)]">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="block w-full text-center px-3 py-2 rounded-xl text-sm font-semibold text-primary-100 hover:bg-white/[0.06] transition-colors"
              data-testid="notification-bell-view-all"
            >
              {t('notificationsBell.viewAll', { defaultValue: 'View all notifications' })}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// Type → icon/color map (kept at module scope so we don't re-create it on
// every render). All icons use the lime primary tint; "system" stays muted.
const TYPE_META = {
  session_logged: { icon: Mail },
  session_confirmed: { icon: Mail },
  badge_earned: { icon: Award },
  level_up: { icon: TrendingUp },
  challenge_completed: { icon: Target },
  reward_redeemed: { icon: Gift },
  team_invite: { icon: Users2 },
  message_received: { icon: MessageCircle },
  mention: { icon: AtSign },
  system: { icon: Info },
};

export default NotificationBell;
